import { faMinusSquare, faPlusSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuItem } from "@mui/material";
import { Loading, Row } from "@repo/ui";
import { MouseEvent, useCallback, useMemo, useState } from "react";

import { useOnboardingSessionsApi } from "../../api/onboardingSessionsApi";
import * as Queries from "../../api/queries";
import { useQueryWrapper } from "../../api/repPortalApi";
import { useRepSegmentsApi } from "../../api/repSegmentsApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { useSegmentContext } from "../../contexts/segmentContext";
import OBCompletion, { OnboardingRequirements } from "../../models/onboardingRequirement";
import { ReqIcon } from "../../tabs/onboardingSessions/req";
import style from "./style.module.scss";

export function SingleOnboardingCompletion(props: SingleOnboardingCompletionProps) {
	const { repId } = useRepContext();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { data: repSegments } = useRepSegmentsQuery();
	const { segmentId } = useSegmentContext();
	const filteredRepSegments = useMemo(() => {
		if (!repSegments) return [];
		if (!segmentId) return repSegments;
		return repSegments.filter((s) => s.id === segmentId);
	}, [repSegments, segmentId]);

	const { onboardingRequirementsQuery } = useOnboardingSessionsApi();
	const { loading } = onboardingRequirementsQuery;

	const count = filteredRepSegments.length;

	return (
		<Loading loading={loading} tag="h5">
			<div className={style.containerSingle}>
				{filteredRepSegments?.map((s) => (
					<OnboardingCompletion
						{...props}
						label={count < 2 ? "" : s.stateName}
						repSegmentId={s.id}
						key={s.id}
					/>
				))}
			</div>
		</Loading>
	);
}

export function OnboardingCompletion(props: Props) {
	const { repId } = useRepContext();
	const { depthToDisplay = 2, req, repSegmentId, label } = props;
	const { onboardingRequirementsQuery } = useOnboardingSessionsApi();
	const { data, loaded, loading } = onboardingRequirementsQuery;

	const requirement = data?.[req];
	return (
		<Loading loading={loading} tag="h5">
			<div className={style.container}>
				<span className="font-bold">
					{requirement?.onboardingCompletion?.[repSegmentId].required ? label : ""}
				</span>
				<OnboardingCompletionInternal
					{...props}
					loaded={loaded}
					completion={requirement?.onboardingCompletion?.[repSegmentId]}
					depth={2 - depthToDisplay}
				/>
			</div>
		</Loading>
	);
}

function OnboardingCompletionInternal(props: InternalProps) {
	const { completion, entityId, loaded, depth } = props;
	const [open, setOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLElement>();
	const handleClick = useCallback(
		(e: MouseEvent<HTMLElement>) => {
			if (open) close();
			else openFn(e);
		},
		[open],
	);
	function openFn(e: MouseEvent<HTMLElement>) {
		setOpen(true);
		setAnchorEl(e.currentTarget.parentElement);
	}
	function close() {
		setOpen(false);
		setAnchorEl(null);
	}

	// TODO: If top level and entityId, make sure to display the iteration completion.  (Top level passed, but child isn't, how to handle display???)
	// TODO: (Sam) If entityId is provided, the app will freeze.  Need to figure out how to handle this.
	// TODO: (Sam) Combine the logic here and with onboarding sessions req component.

	const isIterated = useMemo(() => completion && Object.keys(completion.iterated).length > 0, [completion]);
	if (!loaded || !completion) return null;
	return (
		<>
			<SingleRequirement
				{...props}
				open={open}
				openMenu={openFn}
				toggleMenu={handleClick}
				hasSubs={depth > 0 && (isIterated || completion.subRequirements?.length > 0)}
			/>
			{renderIterated()}
			{renderSubComponents()}
		</>
	);

	function renderIterated() {
		if (!isIterated) return null;
		if (entityId && completion.iterated[entityId]) {
			let v = completion.iterated[entityId];
			if (v.done !== completion.done) {
				v = {
					...completion.iterated[entityId],
					name: `${completion.name} (this)`,
					required: completion.required,
				};
			}
			return (
				<OnboardingCompletionInternal
					key={`${v.name}-${entityId}`}
					{...props}
					completion={v}
					open={open}
					anchorEl={anchorEl}
				/>
			);
		}
		if (depth < 0)
			return (
				<Row>
					{Object.keys(completion.iterated).map((k, i) => {
						const v = {
							...completion.iterated[k],
							name: `${completion.name} ${i + 1}`,
							required: completion.required,
						};
						if (!completion.required) return null;
						return (
							<OnboardingCompletionInternal
								{...props}
								completion={v}
								depth={depth + 1}
								key={`${v.name}-${k}`}
							/>
						);
					})}
				</Row>
			);
		return (
			<Menu open={open} anchorEl={anchorEl || props.anchorEl} onClose={close} className={style.menu}>
				{Object.keys(completion.iterated).map((k, i) => {
					const v = {
						...completion.iterated[k],
						name: `${completion.name} ${i + 1}`,
						required: completion.required,
					};
					if (!completion.required) return null;
					return (
						<MenuItem key={`${v.name}-${k}`}>
							<OnboardingCompletionInternal {...props} completion={v} depth={depth + 1} />
						</MenuItem>
					);
				})}
			</Menu>
		);
	}

	function renderSubComponents() {
		if (isIterated) return null;
		if (completion.done) return null;
		if (!completion.subRequirements?.length) return null;
		if (depth < 1)
			return (
				<Row>
					{completion.subRequirements
						.filter((r) => r.required)
						.map((v) => (
							<OnboardingCompletionInternal
								{...props}
								completion={v}
								open={false}
								depth={depth + 1}
								key={`${v.name}-${entityId}`}
							/>
						))}
				</Row>
			);
		return (
			<Menu
				open={props.open || open}
				anchorEl={anchorEl || props.anchorEl}
				className={style.menu}
				onClose={close}
			>
				{completion.subRequirements
					.filter((r) => r.required)
					.map((v) => (
						<MenuItem key={v.name}>
							<OnboardingCompletionInternal
								key={`${v.name}-${entityId}`}
								{...props}
								completion={v}
								open={false}
								depth={depth + 1}
							/>
						</MenuItem>
					))}
			</Menu>
		);
	}
}

function SingleRequirement(props: SingleProps) {
	const { completion, loaded, hasSubs } = props;
	const { name, required } = completion;
	if (!loaded || !required || !name) return null;

	return (
		<Loading loading={!loaded} tag="span">
			<div className="flex items-center gap-1">
				<ReqIcon completion={completion} />
				{name}
				{hasSubs && !completion.done && (
					<FontAwesomeIcon icon={props.open ? faMinusSquare : faPlusSquare} onClick={props.toggleMenu} />
				)}
			</div>
		</Loading>
	);
}

interface SingleOnboardingCompletionProps {
	req: keyof OnboardingRequirements;
	entityId?: number;
	depthToDisplay: number;
}

interface Props extends SingleOnboardingCompletionProps {
	label?: string;
	repSegmentId: number;
}

interface InternalProps extends SingleProps {
	entityId?: number;
}

interface SingleProps {
	completion?: OBCompletion;
	loaded: boolean;
	open?: boolean;
	openMenu?: (e: MouseEvent<Element>) => void;
	toggleMenu?: (e: MouseEvent<Element>) => void;
	anchorEl?: HTMLElement;
	depth: number;
	hasSubs?: boolean;
}
